import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { Plus, Trash2, Pencil, CheckSquare, Square } from 'lucide-react';

const PRIORITY_BG = { low: 'var(--bluish)', medium: 'var(--yellow-msg)', high: 'var(--red-msg)' };
const PRIORITY_TEXT = { low: 'var(--text-active)', medium: '#946200', high: 'var(--error)' };

const Tasks = () => {
  const { user } = useAuth();
  const { lobs } = useData();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [lobFilter, setLobFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setErr('');
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (lobFilter && lobFilter !== '__general__') params.set('lob_id', lobFilter);
    api.get(`/tasks?${params.toString()}`).then((r) => setTasks(r.data)).catch((e) => setErr(formatApiError(e))).finally(() => setLoading(false));
  }, [statusFilter, lobFilter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.role === 'super_admin') api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  }, [user?.role]);

  const visible = lobFilter === '__general__' ? tasks.filter((t) => !t.lob_id) : tasks;
  const lobLabel = (id) => lobs.find((l) => l.id === id)?.label;
  const userName = (id) => users.find((u) => u.id === id)?.name || (id === user?.id ? (user?.name || 'Me') : 'Unassigned');

  const toggle = async (t) => {
    try { await api.patch(`/tasks/${t.id}`, { status: t.status === 'completed' ? 'open' : 'completed' }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await api.delete(`/tasks/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="tasks-toolbar">
        <span className="page-title">Tasks</span>
        <select className="sel-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="task-status-filter">
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="completed">Completed</option>
        </select>
        <select className="sel-input" value={lobFilter} onChange={(e) => setLobFilter(e.target.value)} data-testid="task-lob-filter">
          <option value="">All business lines</option>
          <option value="__general__">General (no LOB)</option>
          {lobs.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
        <button className="cta-btn" onClick={() => setShowModal(true)} data-testid="add-task-btn">
          <Plus size={11} style={{ marginRight: 4 }} />New task
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="tasks-error">{err}</div>}

      <div className="content-area" data-testid="tasks-content">
        {loading && visible.length === 0 && <div className="empty-state">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="empty-state" data-testid="tasks-empty">
            <div className="ttl">No tasks</div>
            <div className="sub">Click <b>New task</b> to create your first.</div>
          </div>
        )}
        {visible.map((t) => (
          <div key={t.id} className="card" style={{ marginBottom: 8, padding: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid={`task-row-${t.id}`}>
            <button className="btn-link" style={{ padding: 0, marginTop: 1 }} onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}>
              {t.status === 'completed' ? <CheckSquare size={15} style={{ color: 'var(--success)' }} /> : <Square size={15} style={{ color: 'var(--text-faded)' }} />}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, textDecoration: t.status === 'completed' ? 'line-through' : 'none', opacity: t.status === 'completed' ? 0.5 : 1 }}>{t.title}</div>
              {t.description && <div style={{ fontSize: 11, color: 'var(--text-faded)', marginTop: 2 }}>{t.description}</div>}
              <div style={{ fontSize: 10, color: 'var(--text-faded)', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {t.due_date && <span data-testid={`task-due-${t.id}`}>Due {t.due_date}</span>}
                <span className="tag-pill" style={{ background: PRIORITY_BG[t.priority], color: PRIORITY_TEXT[t.priority], fontSize: 9 }}>{t.priority}</span>
                <span>{t.lob_id ? (lobLabel(t.lob_id) || 'LOB') : 'General'}</span>
                {t.assignee_id && <span>Assigned: {userName(t.assignee_id)}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn-link" style={{ color: 'var(--text-faded)', padding: 0 }} onClick={() => setEditTask(t)} data-testid={`task-edit-${t.id}`}><Pencil size={12} /></button>
              <button className="btn-link" style={{ color: 'var(--text-faded)', padding: 0 }} onClick={() => del(t.id)} data-testid={`task-delete-${t.id}`}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {(showModal || editTask) && (
        <TaskModal task={editTask} lobs={lobs} users={users} isAdmin={user?.role === 'super_admin'} onClose={(saved) => { setShowModal(false); setEditTask(null); if (saved) load(); }} />
      )}
    </>
  );
};

export const TaskModal = ({ task, lobs, users, isAdmin, onClose }) => {
  const isEdit = !!(task && task.id);
  const [form, setForm] = useState(task ? { ...task, lob_id: task.lob_id || '', contact_id: task.contact_id || '' } : {
    title: '', description: '', due_date: '', priority: 'medium', lob_id: '', contact_id: '', assignee_id: '',
  });
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.lob_id) api.get(`/contacts?lob_id=${form.lob_id}`).then((r) => setContacts(r.data)).catch(() => setContacts([]));
    else setContacts([]);
  }, [form.lob_id]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const payload = {
      title: form.title, description: form.description || null, due_date: form.due_date || null,
      priority: form.priority, lob_id: form.lob_id || null, contact_id: form.contact_id || null,
      assignee_id: form.assignee_id || null,
    };
    try {
      if (isEdit) await api.patch(`/tasks/${task.id}`, payload);
      else await api.post('/tasks', payload);
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="task-modal">
        <div className="modal-title">{isEdit ? 'Edit task' : 'New task'}</div>
        <label className="f-label">Title</label>
        <input className="f-input" value={form.title} onChange={(e) => set('title', e.target.value)} required data-testid="tm-title" />
        <label className="f-label">Description</label>
        <textarea className="f-textarea" rows={2} value={form.description || ''} onChange={(e) => set('description', e.target.value)} data-testid="tm-description" />
        <div className="modal-row">
          <div><label className="f-label">Due date</label><input className="f-input" type="date" value={form.due_date || ''} onChange={(e) => set('due_date', e.target.value)} data-testid="tm-due" /></div>
          <div><label className="f-label">Priority</label>
            <select className="f-select" value={form.priority} onChange={(e) => set('priority', e.target.value)} data-testid="tm-priority">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Business line</label>
            <select className="f-select" value={form.lob_id || ''} onChange={(e) => { set('lob_id', e.target.value); set('contact_id', ''); }} data-testid="tm-lob">
              <option value="">General (no LOB)</option>
              {lobs.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
          <div><label className="f-label">Link to contact</label>
            <select className="f-select" value={form.contact_id || ''} onChange={(e) => set('contact_id', e.target.value)} disabled={!form.lob_id} data-testid="tm-contact">
              <option value="">— None —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.first} {c.last}</option>)}
            </select>
          </div>
        </div>
        {isAdmin && (
          <>
            <label className="f-label">Assign to</label>
            <select className="f-select" value={form.assignee_id || ''} onChange={(e) => set('assignee_id', e.target.value)} data-testid="tm-assignee">
              <option value="">Myself</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </>
        )}
        {err && <div className="f-error" data-testid="tm-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} data-testid="tm-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="tm-submit">{busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

export default Tasks;
